import axios from "axios";
import { getFirebaseAuthorizationHeaders } from "./firebase-auth-headers";
import type { Auth, User } from "firebase/auth";
import { sendLocalizedEmailVerification } from "./localized-firebase-email";
import type { LocaleCode } from "../../i18n/localeRegistry";
import {
  JUNGLE_BACKEND_DOMAIN_FOR_ROUTES_MIGRATED_TO_CLOUDFLARE_WORKER,
  JUNGLE_CLOUDFLARE_BASE_URL,
  UserData,
} from "../../constants/GlobalTypesAndEnumsAndConstants";
import { trackError } from "../../utils/analytics/track-error";
import { trackEvent } from "./helper-methods";

const USER_SYNC_COOLDOWN_MS = 10000;
const lastUserSyncTimestampByUserId = new Map<string, number>();
const inFlightUserSyncRequestByUserId = new Map<string, Promise<void>>();
const pendingUserSyncByUserId = new Set<string>();
const scheduledTrailingUserSyncTimeoutIdByUserId = new Map<string, ReturnType<typeof setTimeout>>();

const scheduleTrailingUserSync = (userId: string) => {
  if (scheduledTrailingUserSyncTimeoutIdByUserId.has(userId)) {
    return;
  }

  const nowTimestampMs = Date.now();
  const lastSyncTimestampMs = lastUserSyncTimestampByUserId.get(userId);
  const earliestNextSyncTimestampMs =
    typeof lastSyncTimestampMs === "number" ? lastSyncTimestampMs + USER_SYNC_COOLDOWN_MS : nowTimestampMs;
  const delayMs = Math.max(0, earliestNextSyncTimestampMs - nowTimestampMs);

  const timeoutId = setTimeout(async () => {
    scheduledTrailingUserSyncTimeoutIdByUserId.delete(userId);

    if (!pendingUserSyncByUserId.has(userId)) {
      return;
    }

    // If the last sync request is still in-flight, wait for it so the trailing sync
    // has the best chance of capturing the final Firestore state after a burst of updates.
    const existingInFlightRequest = inFlightUserSyncRequestByUserId.get(userId);
    if (existingInFlightRequest) {
      try {
        await existingInFlightRequest;
      } catch (_error) {}
    }

    pendingUserSyncByUserId.delete(userId);
    await syncUserToSmashSend(userId);
  }, delayMs);

  scheduledTrailingUserSyncTimeoutIdByUserId.set(userId, timeoutId);
};

export interface SendEmailRequest {
  to: string | string[];
  template: string;
  variables?: Record<string, any>;
  reply_to?: string | string[];
}

export interface SendHtmlEmailRequest {
  to: string | string[];
  from: string;
  from_name?: string;
  reply_to?: string;
  subject: string;
  html?: string;
  text?: string;
  track_clicks?: boolean;
  track_opens?: boolean;

  // SMASHSEND uses this value to show analytics for the same type of emails.
  // so it's perfect for having aggregated analytics for the same type of emails.
  group_by?: string;
}

export interface EmailResponse {
  status: "success" | "error";
  message: string;
  errorCode?: string;
  data?: {
    messageId: string;
    status: string;
    to: string;
  };
}

const EMAIL_REQUEST_MAX_ATTEMPTS = 4;

const getEmailErrorResponseData = (error: unknown): Record<string, unknown> | null => {
  if (!axios.isAxiosError(error) || !error.response?.data || typeof error.response.data !== "object") {
    return null;
  }

  return error.response.data as Record<string, unknown>;
};

const shouldRetryEmailRequest = (error: unknown): boolean => {
  if (!axios.isAxiosError(error)) {
    return false;
  }

  // A request that never received a response may have failed because of a
  // temporary connection issue, so the existing bounded retry behavior still
  // helps. Once the server responds, retry only transient HTTP failures.
  if (!error.response) {
    return true;
  }

  const responseErrorCode = getEmailErrorResponseData(error)?.errorCode;
  const retryAfterHeader = error.response.headers?.["retry-after"];
  if (responseErrorCode === "endpoint_temporarily_disabled" || retryAfterHeader !== undefined) {
    return false;
  }

  const responseStatus = error.response.status;
  return responseStatus === 408 || responseStatus === 425 || responseStatus === 429 || responseStatus >= 500;
};

/**
 * Send a template-based email using SMASHSEND Email API
 *
 * ✅ RECOMMENDED: Use this for all emails
 * Templates ensure consistent branding and are easier to maintain.
 *
 * @param emailRequest - Email request parameters
 * @param user - Optional user for tracking
 * @returns Promise<EmailResponse>
 */
export const sendEmail = async (emailRequest: SendEmailRequest, user?: UserData): Promise<EmailResponse> => {
  const requestStartedAtMilliseconds = Date.now();
  let totalAttempts = 0;
  let lastError: unknown = null;
  let lastErrorResponseData: Record<string, unknown> | null = null;

  while (totalAttempts < EMAIL_REQUEST_MAX_ATTEMPTS) {
    totalAttempts += 1;
    try {
      const { data } = await axios.post(
        `${JUNGLE_BACKEND_DOMAIN_FOR_ROUTES_MIGRATED_TO_CLOUDFLARE_WORKER}/emails/send_email`,
        emailRequest,
        {
          headers: { "Content-Type": "application/json" },
          timeout: 10000,
        }
      );

      if (data.status === "success") {
        return data as EmailResponse;
      }

      lastError = new Error("Email service returned an error response");
      lastErrorResponseData = data && typeof data === "object" ? (data as Record<string, unknown>) : null;
      break;
    } catch (error: unknown) {
      lastError = error;
      if (totalAttempts >= EMAIL_REQUEST_MAX_ATTEMPTS || !shouldRetryEmailRequest(error)) {
        break;
      }

      const retryDelayMilliseconds = Math.pow(2, totalAttempts - 1) * 1000;
      await new Promise((resolve) => setTimeout(resolve, retryDelayMilliseconds));
    }
  }

  const responseData = getEmailErrorResponseData(lastError) || lastErrorResponseData;
  const responseErrorCode = typeof responseData?.errorCode === "string" ? responseData.errorCode : undefined;
  const responseMessage = typeof responseData?.message === "string" ? responseData.message : undefined;
  const errorMessage = lastError instanceof Error ? lastError.message : "Unknown error";
  const responseStatus = axios.isAxiosError(lastError) ? lastError.response?.status : undefined;
  const errorKind = responseErrorCode || (responseStatus ? `http_${responseStatus}` : "request_failed");

  trackError(new Error("Template email request failed"), {
    scope: "template-email-send",
    fingerprint: ["template-email-send-failed", errorKind],
    userId: user?.id,
    level: "error",
    tags: {
      email_template: emailRequest.template,
      error_kind: errorKind,
      http_status: responseStatus?.toString() || "no_response",
    },
    extra: {
      totalAttempts,
      durationMilliseconds: Date.now() - requestStartedAtMilliseconds,
      responseStatus,
      responseErrorCode,
    },
  });

  return {
    status: "error",
    message: responseMessage || errorMessage,
    errorCode: responseErrorCode,
    data: undefined,
  };
};

/**
 * ⚠️ DISCOURAGED: Send HTML email with custom content (avoid if possible)
 *
 * Only use this when you absolutely need full control over email content.
 * For most use cases, use sendEmail() with templates instead.
 *
 * Why templates are better:
 * - Consistent branding across all emails
 * - Easier to update designs globally
 * - Better deliverability and spam scores
 * - Less code to maintain
 *
 * @param emailRequest - HTML email request parameters
 * @param user - Optional user for tracking
 * @returns Promise<EmailResponse>
 */
export const sendHtmlEmail = async (emailRequest: SendHtmlEmailRequest, user?: UserData): Promise<EmailResponse> => {
  const requestStartedAtMilliseconds = Date.now();
  let totalAttempts = 0;
  let lastError: unknown = null;
  let lastErrorResponseData: Record<string, unknown> | null = null;

  while (totalAttempts < EMAIL_REQUEST_MAX_ATTEMPTS) {
    totalAttempts += 1;
    try {
      const { data } = await axios.post(
        `${JUNGLE_BACKEND_DOMAIN_FOR_ROUTES_MIGRATED_TO_CLOUDFLARE_WORKER}/emails/send_raw_email`,
        emailRequest,
        {
          headers: { "Content-Type": "application/json" },
          timeout: 30000,
        }
      );

      if (data.status === "success") {
        return data as EmailResponse;
      }

      lastError = new Error("Email service returned an error response");
      lastErrorResponseData = data && typeof data === "object" ? (data as Record<string, unknown>) : null;
      break;
    } catch (error: unknown) {
      lastError = error;
      if (totalAttempts >= EMAIL_REQUEST_MAX_ATTEMPTS || !shouldRetryEmailRequest(error)) {
        break;
      }

      const retryDelayMilliseconds = Math.pow(2, totalAttempts - 1) * 1000;
      await new Promise((resolve) => setTimeout(resolve, retryDelayMilliseconds));
    }
  }

  const responseData = getEmailErrorResponseData(lastError) || lastErrorResponseData;
  const responseErrorCode = typeof responseData?.errorCode === "string" ? responseData.errorCode : undefined;
  const responseMessage = typeof responseData?.message === "string" ? responseData.message : undefined;
  const errorMessage = lastError instanceof Error ? lastError.message : "Unknown error";
  const responseStatus = axios.isAxiosError(lastError) ? lastError.response?.status : undefined;
  const errorKind = responseErrorCode || (responseStatus ? `http_${responseStatus}` : "request_failed");

  trackError(new Error("HTML email request failed"), {
    scope: "html-email-send",
    fingerprint: ["html-email-send-failed", errorKind],
    userId: user?.id,
    level: "error",
    tags: {
      error_kind: errorKind,
      http_status: responseStatus?.toString() || "no_response",
    },
    extra: {
      totalAttempts,
      durationMilliseconds: Date.now() - requestStartedAtMilliseconds,
      responseStatus,
      responseErrorCode,
    },
  });

  return {
    status: "error",
    message: responseMessage || errorMessage,
    errorCode: responseErrorCode,
    data: undefined,
  };
};

export const syncUserToSmashSend = async (userId: string) => {
  if (!userId) {
    return;
  }

  const existingInFlightRequest = inFlightUserSyncRequestByUserId.get(userId);
  if (existingInFlightRequest) {
    pendingUserSyncByUserId.add(userId);
    scheduleTrailingUserSync(userId);
    return existingInFlightRequest;
  }

  const nowTimestampMs = Date.now();
  const lastSyncTimestampMs = lastUserSyncTimestampByUserId.get(userId);
  const isWithinCooldownWindow =
    typeof lastSyncTimestampMs === "number" && nowTimestampMs - lastSyncTimestampMs < USER_SYNC_COOLDOWN_MS;
  if (isWithinCooldownWindow) {
    pendingUserSyncByUserId.add(userId);
    scheduleTrailingUserSync(userId);
    return;
  }

  lastUserSyncTimestampByUserId.set(userId, nowTimestampMs);

  const userSyncRequestPromise = (async () => {
    try {
      const payload = {
        user_id: userId,
      };

      await axios.post(`${JUNGLE_CLOUDFLARE_BASE_URL}/users/sync`, payload, {
        headers: {
          "Content-Type": "application/json",
        },
        timeout: 10000,
      });
    } catch (error) {
      console.log("Error: Failed to sync user to SmashSend:", error);
    } finally {
      inFlightUserSyncRequestByUserId.delete(userId);
    }
  })();

  inFlightUserSyncRequestByUserId.set(userId, userSyncRequestPromise);
  await userSyncRequestPromise;
};

/**
 * Fire a one-time verification email for a partner-created Firebase user
 * after they land via /auth-by-token/:token with ?verify=1 set.
 *
 * Partner-created users (e.g. handed off from Studyly via the
 * /partners/signin-link endpoint on jungle-backend) are minted server-side
 * with no password and email_verified=false. Calling sendEmailVerification
 * once here puts them in the same state as a direct signup as soon as they
 * click the link in the email, which keeps any downstream branching on
 * emailVerified consistent and gives them a permanent return path via
 * "Forgot password" / verification email.
 *
 * Fire-and-forget: errors are tracked but never thrown, since the user has
 * already successfully signed in by the time this runs.
 */
export const sendPartnerSigninVerificationEmail = (auth: Auth, firebaseUser: User | null, locale?: LocaleCode): void => {
  if (!firebaseUser || firebaseUser.emailVerified) {
    return;
  }
  sendLocalizedEmailVerification(auth, firebaseUser, locale).catch((sendVerificationEmailError: unknown) => {
    const errorMessageString =
      sendVerificationEmailError instanceof Error
        ? sendVerificationEmailError.message
        : String(sendVerificationEmailError);
    trackEvent("Error: Failed to send partner-signin verification email", {
      errorMessage: errorMessageString,
    });
  });
};

export async function requestQuestionEmailExport(
  deckId: string,
  requestId: string
): Promise<{ status: "success" | "error"; errorCode?: string }> {
  try {
    const response = await axios.post(
      `${JUNGLE_CLOUDFLARE_BASE_URL}/decks/email_questions`,
      { deckId, requestId },
      { headers: await getFirebaseAuthorizationHeaders(), timeout: 60000 }
    );
    return response.data?.status === "success"
      ? { status: "success" }
      : { status: "error", errorCode: response.data?.errorCode };
  } catch (error) {
    const errorCode =
      axios.isAxiosError(error) && typeof error.response?.data?.errorCode === "string"
        ? error.response.data.errorCode
        : "email_acceptance_unknown";
    trackError(new Error("Question email export failed"), {
      scope: "question-email-export",
      tags: { error_kind: errorCode },
    });
    return { status: "error", errorCode };
  }
}
